import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Code2,
  Copy,
  ExternalLink,
  KeyRound,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Power,
  QrCode,
  Trash2,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useTenantModuleEnabled } from "@/entities/dashboard";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { toast } from "@/shared/hooks/use-toast";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import {
  absolutizeEmbedCode,
  absolutizeUrl,
  webEntryService,
  type WebEntryDto,
  type WebEntryUpsert,
} from "../../api/webEntryService";
import { WebEntryFormDialog } from "./WebEntryFormDialog";
import { WebEntryQrDialog } from "./WebEntryQrDialog";

const WEB_ENTRIES_QUERY_KEY = ["cs-web-entries"] as const;

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* 落到 execCommand 兜底 */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

function toUpsert(e: WebEntryDto, patch: Partial<WebEntryUpsert> = {}): WebEntryUpsert {
  return {
    name: e.name,
    agentName: e.agentName,
    welcomeText: e.welcomeText,
    quickQuestions: e.quickQuestions,
    themeColor: e.themeColor,
    leadEnabled: e.leadEnabled,
    enabled: e.enabled,
    // 原值回写(停用 / 启用不能把头像清掉);契约假设:DTO.avatarUrl 是存的原值,不是短时预签地址
    avatarUrl: e.avatarUrl ?? "",
    ...patch,
  };
}

/**
 * 渠道页「网页 / H5」页签(1010 客服 H5 端,web_widget 渠道)。
 * 一个租户可建多个接入点(官网、公众号菜单、海报二维码…),每个接入点一个公开 key。
 * 读 = 租户成员;写(新建 / 编辑 / 换 key / 停用 / 删除)= TENANT_MANAGER+,其余角色只看不改(真正的闸在 api)。
 */
export function WebEntriesTab() {
  const { t } = useTranslation("platform");
  const k = (s: string, o?: Record<string, unknown>) => t(`channelMgmt.webEntry.${s}`, o);
  const { effectiveRole } = useAuth();
  const canWrite = isTenantManagerPlus(effectiveRole);
  const confirm = useConfirm();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WebEntryDto | null>(null);
  const [qrTarget, setQrTarget] = useState<{ name: string; url: string } | null>(null);

  // 接入点接口在 ai_kefu 模块门内:未开通客服舱时不请求(否则 403 弹错),直接给开通提示。
  const kefuEnabled = useTenantModuleEnabled("ai_kefu");
  const q = useQuery({
    queryKey: WEB_ENTRIES_QUERY_KEY,
    queryFn: webEntryService.list,
    enabled: kefuEnabled !== false,
  });
  const entries = q.data ?? [];

  const save = useMutationWithToast<WebEntryDto, { id: string | null; body: WebEntryUpsert }>({
    mutationFn: ({ id, body }) =>
      id ? webEntryService.update(id, body) : webEntryService.create(body),
    successToast: (_d, v) => (v.id ? k("toastUpdated") : k("toastCreated")),
    invalidateKeys: [WEB_ENTRIES_QUERY_KEY],
    onSuccess: () => setFormOpen(false),
  });
  const toggle = useMutationWithToast<WebEntryDto, WebEntryDto>({
    mutationFn: (e) => webEntryService.update(e.id, toUpsert(e, { enabled: !e.enabled })),
    successToast: (d) => (d?.enabled ? k("toastEnabled") : k("toastDisabled")),
    invalidateKeys: [WEB_ENTRIES_QUERY_KEY],
  });
  const rotate = useMutationWithToast<WebEntryDto, string>({
    mutationFn: (id) => webEntryService.rotateKey(id),
    successToast: k("toastRotated"),
    invalidateKeys: [WEB_ENTRIES_QUERY_KEY],
  });
  const remove = useMutationWithToast<void, string>({
    mutationFn: (id) => webEntryService.remove(id),
    successToast: k("toastDeleted"),
    invalidateKeys: [WEB_ENTRIES_QUERY_KEY],
  });

  const copy = async (text: string, okKey: string) => {
    const ok = await copyText(text);
    toast(ok ? { title: k(okKey) } : { title: k("copyFailed"), variant: "destructive" });
  };

  const onRotate = async (e: WebEntryDto) => {
    const ok = await confirm({
      title: k("rotateConfirmTitle", { name: e.name }),
      description: k("rotateConfirmDesc"),
      confirmText: k("rotateConfirmOk"),
      variant: "warning",
    });
    if (ok) rotate.mutate(e.id);
  };
  const onToggle = async (e: WebEntryDto) => {
    if (e.enabled) {
      const ok = await confirm({
        title: k("disableConfirmTitle", { name: e.name }),
        description: k("disableConfirmDesc"),
        confirmText: k("disableConfirmOk"),
        variant: "warning",
      });
      if (!ok) return;
    }
    toggle.mutate(e);
  };
  const onDelete = async (e: WebEntryDto) => {
    const ok = await confirm({
      title: k("deleteConfirmTitle", { name: e.name }),
      description: k("deleteConfirmDesc"),
      confirmText: k("deleteConfirmOk"),
      variant: "destructive",
    });
    if (ok) remove.mutate(e.id);
  };

  return (
    <div className="space-y-4" data-testid="web-entries-tab">
      {/* 说明:三种用法 + 计费口径 */}
      <Card>
        <CardContent className="p-4 space-y-2 text-sm">
          <p className="font-medium text-foreground">{k("introTitle")}</p>
          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
            <li>{k("introLink")}</li>
            <li>{k("introQr")}</li>
            <li>{k("introEmbed")}</li>
          </ul>
          <p className="text-xs text-muted-foreground" data-testid="web-entries-billing">
            {k("introBilling")}
          </p>
        </CardContent>
      </Card>

      {kefuEnabled === false ? (
        <Card>
          <CardContent
            className="p-8 text-center text-sm text-muted-foreground"
            data-testid="web-entries-module-off"
          >
            {k("moduleOff")}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {k("listTitle", { count: entries.length })}
            </p>
            {canWrite && (
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                data-testid="web-entry-create"
              >
                <Plus className="h-4 w-4 mr-1" />
                {k("create")}
              </Button>
            )}
          </div>

          <QueryErrorBanner
            isError={q.isError}
            message={k("loadFailed")}
            onRetry={() => q.refetch()}
          />

          {q.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 && !q.isError ? (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                {canWrite ? k("emptyManager") : k("emptyReadonly")}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => {
                const url = absolutizeUrl(e.h5Url);
                const embed = absolutizeEmbedCode(e.embedCode);
                return (
                  <Card key={e.id} data-testid={`web-entry-${e.id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="h-3 w-3 rounded-full border"
                          style={{ background: e.themeColor }}
                          aria-hidden="true"
                        />
                        <span className="font-medium">{e.name}</span>
                        <Badge variant={e.enabled ? "default" : "secondary"}>
                          {e.enabled ? k("statusOn") : k("statusOff")}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {k("sessions7d", { count: e.sessionCount7d ?? 0 })}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {k("agentLabel")}
                          {e.agentName}
                        </span>
                      </div>
                      <div className="rounded-md bg-muted/40 px-3 py-2 font-mono text-xs break-all">
                        {url}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => copy(url, "copiedLink")}>
                          <Link2 className="h-3.5 w-3.5 mr-1" />
                          {k("copyLink")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setQrTarget({ name: e.name, url })}
                        >
                          <QrCode className="h-3.5 w-3.5 mr-1" />
                          {k("qr")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copy(embed, "copiedEmbed")}
                        >
                          <Code2 className="h-3.5 w-3.5 mr-1" />
                          {k("copyEmbed")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          {k("preview")}
                        </Button>
                        {canWrite && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditing(e);
                                setFormOpen(true);
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1" />
                              {k("edit")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={rotate.isPending}
                              onClick={() => onRotate(e)}
                            >
                              <KeyRound className="h-3.5 w-3.5 mr-1" />
                              {k("rotate")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={toggle.isPending}
                              onClick={() => onToggle(e)}
                            >
                              <Power className="h-3.5 w-3.5 mr-1" />
                              {e.enabled ? k("disable") : k("enable")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={remove.isPending}
                              onClick={() => onDelete(e)}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              {k("delete")}
                            </Button>
                          </>
                        )}
                      </div>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground">
                          <Copy className="inline h-3 w-3 mr-1" />
                          {k("embedPreview")}
                        </summary>
                        <pre className="mt-2 whitespace-pre-wrap break-all rounded-md bg-muted/40 p-2 font-mono">
                          {embed}
                        </pre>
                      </details>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <WebEntryFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        submitting={save.isPending}
        onSubmit={(body) => save.mutate({ id: editing?.id ?? null, body })}
      />
      <WebEntryQrDialog target={qrTarget} onClose={() => setQrTarget(null)} />
    </div>
  );
}
